import axiosInstance from "@/lib/axiosInstance";
import { buildFilterByParam } from "@/utils/buildFilterByParam";
import { locationsEndpoints } from "./locations.endpoints";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import type {
  BrandDropdownResponse,
  DropdownOption,
  LocationDetailsResponse,
  LocationsListQueryOptions,
  LocationsListResponse,
} from "./locations.queries.types";
import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";

export const ClientsLocationQueryKeys = {
  getLocationList: "locationList",
  getLocationById: "locationById",
  getLocationDropdown: "locationDropdown",
} as const;

export const locationQueryKeys = {
  getAllLocations: "allLocations",
  getLocationDetailsById: "locationDetailsById",
} as const;

export const getClientsLocationDropdown = async (
  dropDownFieldName: string,
  _clientId: string | null,
): Promise<DropdownOption[]> => {
  if (!_clientId) {
    return [];
  }

  if (dropDownFieldName === "brandName") {
    try {
      const response = await axiosInstance.get<BrandDropdownResponse>(
        `${locationsEndpoints.clientBrands}?clientId=${_clientId}`,
      );
      return (
        response.data?.data?.data?.map((item) => ({
          value: item.id,
          label: item.label,
        })) ?? []
      );
    } catch (error) {
      console.error("Error fetching brands dropdown:", error);
      return [];
    }
  } else if (dropDownFieldName === "locationType") {
    try {
      const response = await axiosInstance.get(
        locationsEndpoints.getLocationDropdown +
          "?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=client_location_type",
      );
      return (
        response.data?.data?.map((item: any) => ({
          value: String(item.id || item.value || ""),
          label: item.label || "",
        })) || []
      );
    } catch (error) {
      console.error("Error fetching location type dropdown:", error);
      return [];
    }
  }
  return [];
};

export const getLocationFilterDropdown = async (
  _type: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
  searchParams?: TSearchParams,
): Promise<any> => {
  const params = new URLSearchParams();

  let baseDropdownUrl = locationsEndpoints.getLocationDropdown;

  if (search) {
    params.append("search", search);
  }

  if (key === "siteId") {
    params.append("type", "clientLocations");
    params.append("field", "siteId");
    params.append("distinct", "true");
    params.append("modify", JSON.stringify({ source: ["siteId"], target: "label" }));
    if (searchParams?.filterValue) {
      params.append("filterBy", "clientId");
      params.append("filterValue", String(searchParams.filterValue));
    }
  }

  if (key === "status") {
    baseDropdownUrl = locationsEndpoints.lookups;
    params.append("type", locationsEndpoints.lookupTypes.clientLocationStatus);
  }

  if (key === "timezone") {
    baseDropdownUrl = locationsEndpoints.lookups;
    params.append("type", locationsEndpoints.lookupTypes.timezone);
  }

  if (key === "locationsType") {
    params.append("type", locationsEndpoints.lookupTypes.clientLocationType);
    if (searchParams?.clientId) {
      params.append("clientId", String(searchParams.clientId));
    }

    const queryString = getQueryParamsWithCustomQueryObj(params);
    const { data } = await axiosInstance.get<any>(`${locationsEndpoints.lookups}?${queryString}`, {
      signal,
    });
    return data;
  }

  if (key === "brand") {
    if (searchParams?.clientId) {
      params.append("clientId", String(searchParams.clientId));
    }
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }

    const queryString = getQueryParamsWithCustomQueryObj(params);
    const { data } = await axiosInstance.get<any>(
      `${locationsEndpoints.clientBrands}?${queryString}`,
      { signal },
    );
    return data;
  }

  if (key === "clientName") {
    params.append("type", "Clients");
    params.append("field", "id");
    params.append("field", "businessName");
  }

  const clientScopedDropdownPath: Record<string, string> = {
    activeWork: locationsEndpoints.locationActiveWorkOrders,
    zipcode: locationsEndpoints.locationZipcodes,
    phone: locationsEndpoints.locationPhones,
  };
  const clientScopedPath = key ? clientScopedDropdownPath[key] : undefined;
  if (clientScopedPath) {
    const clientId = searchParams?.filterValue;
    if (clientId) {
      baseDropdownUrl = `${locationsEndpoints.clientDropdown}/${clientId}/${clientScopedPath}`;
    }
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }
  }

  if (key === "assetId") {
    params.append("type", "ClientAssets");
    params.append("field", "id");
    params.append("field", "assetId");
    params.append("alias", JSON.stringify({ source: "assetId", target: "label" }));
  }

  if (key === "assetType") {
    params.append("type", "LookupTypes");
    params.append("field", "label");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "client_asset_type");
  }

  if (key === "manufacturerBrand") {
    params.append("type", "LookupTypes");
    params.append("field", "label");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "client_asset_manufacturer");
  }

  if (key === "modelNumber") {
    params.append("type", "ClientAssets");
    params.append("field", "id");
    params.append("field", "modelNumber");
    params.append("alias", JSON.stringify({ source: "modelNumber", target: "label" }));
  }

  if (key === "serialNumber") {
    params.append("type", "ClientAssets");
    params.append("field", "id");
    params.append("field", "serialNumber");
    params.append("alias", JSON.stringify({ source: "serialNumber", target: "label" }));
  }

  if (key === "locationInstalled") {
    params.append("type", "ClientLocations");
    params.append("field", "id");
    params.append("field", "siteId");
    params.append("alias", JSON.stringify({ source: "siteId", target: "locationInstalled" }));
    if (searchParams?.filterValue) params.append("filterValue", searchParams?.filterValue);
    params.append("filterBy", "id");
  }

  if (key === "activeWork") {
    params.append("type", "ClientLocations");
    params.append("field", "activeWorkOrdersCount");
    params.append(
      "alias",
      JSON.stringify({ source: "activeWorkOrdersCount", target: "label", asString: true }),
    );

    params.append(
      "modify",
      JSON.stringify({
        source: ["activeWorkOrdersCount"],
        target: "value",
        asString: true,
      }),
    );
    params.append("distinct", "true");
  }

  if (key === "primaryContact") {
    params.append("type", "ClientContacts");
    params.append("field", "id");
    params.append("field", "firstName");
    params.append("field", "lastName");
    params.append(
      "modify",
      JSON.stringify({
        source: ["firstName", "lastName"],
        target: "contactName",
        delimiter: " ",
      }),
    );
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }
  }

  if (key === "address") {
    baseDropdownUrl = locationsEndpoints.clientLocationAddresses;
  }

  if (key === "zipcodes") {
    baseDropdownUrl = locationsEndpoints.clientLocationAddressesZipcode;
    params.append("type", "zipcode");
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }
  }

  if (key === "clientLocation-allAddresses") {
    if (searchParams?.limit) {
      params.append("limit", String(searchParams.limit));
    }
    if (searchParams?.clientId) {
      params.append("clientId", String(searchParams.clientId));
    }

    const queryString = getQueryParamsWithCustomQueryObj(params);
    const { data } = await axiosInstance.get<any>(
      `${locationsEndpoints.clientLocationAddresses}?${queryString}`,
      { signal },
    );
    return data;
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  const { data } = await axiosInstance.get<any>(`${baseDropdownUrl}?${queryString}`, {
    signal,
  });

  return data;
};

export const getClientLocationList = async ({
  clientId,
  search,
  sortBy,
  sortOrder,
  page,
  pageSize,
  signal,
}: {
  clientId: string | null;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: string;
  pageSize?: string;
  signal?: AbortSignal;
}) => {
  try {
    const queryParams: Record<string, string> = {
      page: page?.toString() ?? "1",
      limit: pageSize?.toString() ?? "10",
    };

    if (search) {
      queryParams.search = search;
    }

    if (sortBy) {
      queryParams.sortBy = sortBy;
    }

    if (sortOrder) {
      queryParams.sortOrder = sortOrder;
    }

    if (!clientId) {
      return { data: [], pagination: { total: 0, totalPages: 0, page: 1, limit: 10 } };
    }

    const response = await axiosInstance.get(
      `${locationsEndpoints.getLocationList}/${clientId}?${getQueryParamsWithCustomQueryObj(queryParams)}`,
      { signal },
    );
    return (
      response.data?.data || {
        data: [],
        pagination: { total: 0, totalPages: 0, page: 1, limit: 10 },
      }
    );
  } catch (error) {
    console.error("Error fetching client location list:", error);
    return { data: [], pagination: { total: 0, totalPages: 0, page: 1, limit: 10 } };
  }
};

export const getLocationById = async (_clientId: string | null, _locationId: string) => {
  if (!_clientId) {
    return [];
  }

  try {
    const response = await axiosInstance.get(
      locationsEndpoints.getLocationList + "/" + _clientId + "/" + _locationId,
    );
    return response?.data?.data || [];
  } catch (error) {
    console.error("Error fetching location by id:", error);
    return [];
  }
};

export const fetchAllLocations = async (
  clientId: string,
  options: LocationsListQueryOptions,
  signal: AbortSignal,
): Promise<LocationsListResponse> => {
  const params = new URLSearchParams();

  params.append("page", options.page.toString());
  params.append("limit", options.pageSize.toString());

  if (options.search) {
    params.append("search", options.search);
  }

  if (options.sortBy) {
    params.append("sortBy", options.sortBy);
  }

  if (options.sortOrder) {
    params.append("sortOrder", options.sortOrder);
  }

  const filterBy = buildFilterByParam(options.filters);
  if (filterBy) {
    params.append("filterBy", filterBy);
  }

  const { data } = await axiosInstance.get<LocationsListResponse>(
    `${locationsEndpoints.getAllLocations}/${clientId}?${getQueryParamsWithCustomQueryObj(params)}`,
    {
      signal,
    },
  );

  return data;
};

export const fetchLocationDetailsById = async (
  clientId: string,
  locationId: string,
): Promise<LocationDetailsResponse> => {
  const { data } = await axiosInstance.get<LocationDetailsResponse>(
    `${locationsEndpoints.getAllLocations}/${clientId}/${locationId}`,
  );
  return data;
};
